import React, { Fragment, useMemo } from 'react';
import PropTypes from 'prop-types';
import { injectIntl, intlShape } from 'react-intl';
import styled from 'styled-components';
import Subtitle from '@components/Subtitle';
import SectionNote from '@components/SectionNote';
import Swiper from '@components/Swiper';
import MenuSectionImage from '@conversion/components/MenuSectionImage';
import {
  SHELVES_PRODUCTS_EXPAND_MIN_QUANTITY,
  SHELVES_PRODUCTS_MAX_QUANTITY,
} from '@shopDetails/constants';
import ProductCardContainer, {
  getProductCardProps,
} from '@results/HOCs/ProductCardContainer';
import LazyList from '@components/LazyList';

import ShelvesItem from './ShelvesItem';

import messages from './messages';

const getSections = menu =>
  menu?.sections
    ? menu.sections.reduce((acc, section) => {
        if (section.products.length > 0) acc.push(section);
        return acc;
      }, [])
    : [];

const getActionMessage = (section, hasToValidateAge) =>
  section?.products.length >= SHELVES_PRODUCTS_EXPAND_MIN_QUANTITY &&
  !(section.requiresAgeCheck && hasToValidateAge)
    ? 'Ver todos >'
    : '';

const SwiperContainer = styled(Swiper)`
  & > * {
    margin-right: ${({ theme }) => theme.constants.lateralSpace};
    margin-top: 0;
  }
  & > *:first-child {
    margin-left: 0;
  }
`;

const SwiperTitle = styled(Subtitle)`
  margin-bottom: 0;
  margin-top: 25px;
  & button {
    padding-right: 0;
  }
`;

function ShelvesSections({
  intl: { formatMessage },
  menu,
  countryCode,
  onShelveExpand,
  onBeforeProductConfig,
  openAgeValidation,
  hasToValidateAge,
  onSectionHeightUpdated,
  sectionsHeights,
  businessType,
  measurementItems,
  shopAcceptsPreOrder,
  shopIsOpen,
}) {
  const sections = useMemo(() => getSections(menu), [menu]);

  return (
    <LazyList
      data={sections}
      defaultItemHeight="292px"
      itemsPerSection={3}
      onSectionHeightUpdated={onSectionHeightUpdated}
      initialSectionsHeights={sectionsHeights}
    >
      {section => (
        <Fragment key={section.id}>
          <SwiperTitle
            label={section.name}
            separator={false}
            actionText={getActionMessage(section, hasToValidateAge)}
            onActionClick={onShelveExpand(section)}
            withMarginTop
          />
          <MenuSectionImage image={section.image} country={countryCode} />
          <div className="full_width">
            {section.requiresAgeCheck && hasToValidateAge ? (
              <SectionNote
                message={formatMessage(messages.shopdetails_section_text)}
                actionText={formatMessage(messages.shopdetails_section_link)}
                action={() => openAgeValidation(section)}
              />
            ) : (
              <SwiperContainer id={`shelves-section${section.id}`}>
                {section.products
                  .slice(0, SHELVES_PRODUCTS_MAX_QUANTITY)
                  .map(product => (
                    <ProductCardContainer
                      key={product.id}
                      {...getProductCardProps(product)}
                      onBeforeProductConfig={onBeforeProductConfig}
                    >
                      {onClick => (
                        <ShelvesItem
                          key={product.id}
                          product={product}
                          countryCode={countryCode}
                          onClick={onClick}
                          businessType={businessType}
                          measurementItems={measurementItems}
                          shopAcceptsPreOrder={shopAcceptsPreOrder}
                          shopIsOpen={shopIsOpen}
                        />
                      )}
                    </ProductCardContainer>
                  ))}
              </SwiperContainer>
            )}
          </div>
        </Fragment>
      )}
    </LazyList>
  );
}

ShelvesSections.propTypes = {
  countryCode: PropTypes.string.isRequired,
  hasToValidateAge: PropTypes.bool,
  menu: PropTypes.object,
  openAgeValidation: PropTypes.func.isRequired,
  onShelveExpand: PropTypes.func.isRequired,
  onBeforeProductConfig: PropTypes.func.isRequired,
  intl: intlShape,
  sectionsHeights: PropTypes.object.isRequired,
  onSectionHeightUpdated: PropTypes.func.isRequired,
  businessType: PropTypes.string,
  measurementItems: PropTypes.array,
  shopAcceptsPreOrder: PropTypes.bool,
  shopIsOpen: PropTypes.bool,
};

export default React.memo(injectIntl(ShelvesSections));
